// OnboardingPage — first-run hero. Single CTA → POST /api/v1/sources →
// navigate to /sources/:id. Visual baseline updated to match the latest
// design bundle from claude.ai/design (Onboarding.html + onboarding/oneclick.jsx)
// — existing-sources strip, "ONE-CLICK SETUP" eyebrow with green dot,
// SVG-backed reassure cards, Enter-key shortcut, hero-hint subtext.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TagsEditor from '../components/TagsEditor';
import TopHeader from '../components/TopHeader';
import type { Source } from '@datahub/shared';
import { createSource, listSources } from '../api';
import { TagChip } from '../lib/tag-colors';

type ChannelKind = 'webhook' | 'rest' | 'form' | 'email' | 'upload';

export default function OnboardingPage() {
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [existing, setExisting] = useState<Source[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  // Primary channel — drives where the operator lands after create
  // (e.g. form → opens the form config dialog automatically). The
  // backend always provisions all 5 inputs; this is a UX hint only.
  const [primaryChannel, setPrimaryChannel] = useState<ChannelKind>('webhook');
  // Connector profile picker — preselects the discriminator + suggested
  // merge keys so the auto-connect-bq flow can fan out per-event_type
  // BQ views without operator configuration. Defaults to 'generic_webhook'
  // which falls through to the auto-heuristic.
  // BACKLOG: Minimum-action onboarding · Connector profile registry.
  const [connectorProfile, setConnectorProfile] = useState<string>('generic_webhook');
  const navigate = useNavigate();

  useEffect(() => {
    listSources(50).then(setExisting).catch(() => setExisting([]));
  }, []);

  // Smart tag suggestions from name: "Acme — Salesforce" → { client: 'acme', system: 'salesforce' }
  const suggestedTags = useMemo<Record<string, string>>(() => {
    if (!name.trim()) return {};
    const out: Record<string, string> = {};
    const parts = name.split(/[—\-–|·]/).map(p => p.trim()).filter(Boolean);
    if (parts[0]) out.client = parts[0].toLowerCase().replace(/\s+/g, '_');
    if (parts[1]) {
      const sys = parts[1].toLowerCase();
      const known = ['salesforce', 'hubspot', 'pipedrive', 'stripe', 'shopify', 'intercom', 'mailchimp', 'zendesk', 'jira', 'github', 'minimax'];
      const matched = known.find(k => sys.includes(k));
      if (matched) out.system = matched;
      else out.system = sys.replace(/\s+/g, '_');
    }
    return out;
  }, [name]);

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const finalTags = { ...suggestedTags, ...tags };
      const cp = connectorProfile && connectorProfile !== 'generic_webhook' ? connectorProfile : undefined;
      const s = await createSource(name.trim() || undefined, Object.keys(finalTags).length ? finalTags : undefined, cp);
      // Channel hint: append ?via=<channel> so the source page can auto-
      // open the relevant config (form schema builder, schedule dialog, etc).
      navigate(`/sources/${s.source_id}?via=${primaryChannel}`);
    } catch (err) {
      setCreateError(String(err));
      setCreating(false);
    }
  }

  // Enter / Space anywhere triggers the CTA (per design — keyboard-first onboarding)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (creating) return;
      if (pickerOpen) return; // modal owns Enter/Esc while open
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void handleCreate(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [creating, name, tags, suggestedTags, pickerOpen]);

  function fmtAge(iso: string): string {
    const ms = Date.now() - Date.parse(iso);
    if (!Number.isFinite(ms)) return '';
    if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  }

  const hasExisting = (existing?.length ?? 0) > 0;

  // Drag-and-drop quick-from-file. Drop a CSV/JSON/NDJSON/XLSX onto the
  // page → POSTs to /sources/quick-from-file → navigates to the source
  // detail page (which hosts the SchemaReadyBanner). Two clicks total
  // from "I have a file" to "BigQuery view ready" once the threshold
  // is crossed. BACKLOG: Drag-and-drop area on / (OnboardingPage).
  const [dragOver, setDragOver] = useState(false);
  const [dropping, setDropping] = useState(false);
  async function handleDroppedFile(file: File): Promise<void> {
    setDropping(true);
    try {
      const r = await fetch('/api/v1/sources/quick-from-file', {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': file.name },
        body: file,
      });
      const body = await r.json() as { source_id?: string; error?: { code?: string; detail?: string }; redirect_to?: string };
      if (!r.ok || !body.source_id) {
        setCreateError(body.error?.detail ?? body.error?.code ?? `HTTP ${r.status}`);
        setDropping(false);
        return;
      }
      navigate(body.redirect_to ?? `/sources/${body.source_id}?via=upload`);
    } catch (err) {
      setCreateError(String(err));
      setDropping(false);
    }
  }
  useEffect(() => {
    function onDragOver(e: DragEvent): void {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      setDragOver(true);
    }
    function onDragLeave(e: DragEvent): void {
      if (e.relatedTarget) return; // child enter; ignore
      setDragOver(false);
    }
    function onDrop(e: DragEvent): void {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) void handleDroppedFile(f);
    }
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  return (
    <div>
      {dragOver && (
        <div
          aria-hidden
          style={{
            position: 'fixed', inset: 0, zIndex: 80,
            background: 'oklch(0.55 0.18 250 / 0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{
            padding: '24px 32px',
            background: 'var(--bg-elev)',
            border: '2px dashed var(--accent, oklch(0.55 0.18 250))',
            borderRadius: 12,
            fontSize: 16, fontWeight: 600,
            color: 'var(--ink)',
          }}>
            Drop the file to create a source from it.
          </div>
        </div>
      )}
      {dropping && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 90,
          background: 'oklch(0.18 0.01 250 / 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            padding: '20px 28px',
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: 14, fontWeight: 500, color: 'var(--ink)',
          }}>
            Uploading + creating source…
          </div>
        </div>
      )}
      <TopHeader
        env="Onboarding"
        rightExtra={<Link to="/sources" style={{ color: 'inherit', textDecoration: 'none', fontSize: 12 }}>Sources</Link>}
      />

      <div className="hero">
        {hasExisting && existing && (
          <div className="hero-existing" aria-label="Your existing sources">
            <span className="hero-existing-label">Your sources</span>
            <div className="hero-existing-list">
              {existing.slice(0, 3).map(s => (
                <button
                  key={s.source_id}
                  className="hero-existing-chip"
                  title={`${s.name} · created ${fmtAge(s.created_at)}`}
                  onClick={() => { setPickerQuery(s.name); setPickerOpen(true); }}
                >
                  <span className="hero-existing-dot" />
                  <span className="hero-existing-name">{s.name}</span>
                  <span className="hero-existing-meta">{fmtAge(s.created_at)}</span>
                </button>
              ))}
              <span className="hero-existing-sep" />
              <Link to="/sources" className="hero-existing-view">View all sources →</Link>
            </div>
          </div>
        )}

        {pickerOpen && existing && (
          <SourcesPickerModal
            sources={existing}
            initialQuery={pickerQuery}
            onClose={() => setPickerOpen(false)}
            onPick={(sid) => { setPickerOpen(false); navigate(`/sources/${sid}`); }}
            onConnectNew={() => { setPickerOpen(false); void handleCreate(); }}
            fmtAge={fmtAge}
          />
        )}

        <div className="eyebrow"><span className="dot">●</span> One-click setup</div>
        <h1>
          Connect your tools.<br />
          Start in <span className="mark">a minute</span>.
        </h1>
        <p className="lede">
          No forms, no IT tickets, no setup calls. One click gives you a private address where your apps can send their data — and we start collecting it right away. Name it and tidy it up whenever you're ready.
        </p>

        <div style={{ maxWidth: 560, margin: '0 auto 24px' }}>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder='Name your source — e.g. "Acme — Salesforce"'
            data-testid="onboard-name-input"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '14px 18px',
              fontSize: 15, fontFamily: 'var(--ui)',
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              color: 'var(--ink)',
              outline: 'none',
            }}
          />
          {Object.keys(suggestedTags).length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>auto tags</span>
              {Object.entries(suggestedTags).map(([k, v]) => (
                <span key={k} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px',
                  fontSize: 11.5, fontFamily: 'var(--mono)',
                  background: 'var(--accent-soft)', color: 'oklch(0.42 0.12 150)',
                  border: '1px solid var(--accent-line)',
                  borderRadius: 4,
                }}>{k}={v}</span>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            style={{ marginTop: 8, background: 'transparent', border: 'none', color: 'var(--ink-3)', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}
            title={showAdvanced ? 'Hide custom tags, connector profile, and channel picker' : 'Show custom tags, connector profile, and channel picker'}
          >
            {showAdvanced ? '− customize' : '+ customize'}
          </button>
          {showAdvanced && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <TagsEditor tags={tags} onChange={setTags} compact />
              <ConnectorProfilePicker selected={connectorProfile} onPick={setConnectorProfile} />
              <ChannelPicker selected={primaryChannel} onPick={setPrimaryChannel} />
            </div>
          )}
        </div>

        <button className="btn hero-cta primary" onClick={() => { void handleCreate(); }} disabled={creating}>
          {creating ? 'Creating…' : (hasExisting ? 'Connect another source' : 'Connect my first source')}
          <span className="arrow">→</span>
          <span className="kbd">Enter</span>
        </button>
        {createError && <div style={{ marginTop: 16, color: 'var(--danger)', fontSize: 13 }}>{createError}</div>}
        <div className="hero-hint">
          Works right away <span className="sep">·</span> private &amp; secure by default <span className="sep">·</span> disconnect any time
        </div>

        <div className="reassure">
          <div>
            <div className="ico">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2l5.5 2.5v4c0 3.5-2.4 6-5.5 7-3.1-1-5.5-3.5-5.5-7v-4L8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                <path d="M5.5 8l2 2 3-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="label">Nothing gets lost</div>
            <div className="text">Every event is captured the moment it arrives — safe, unchanged, replayable for years.</div>
          </div>
          <div>
            <div className="ico">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M8 4.5V8l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="label">No setup to start</div>
            <div className="text">Pick a name later, decide who sees what later. None of that gets in the way of collecting today.</div>
          </div>
          <div>
            <div className="ico">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 8s2-4 6-4 6 4 6 4-2 4-6 4-6-4-6-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
              </svg>
            </div>
            <div className="label">You'll always know where things stand</div>
            <div className="text">Each event is traceable end-to-end. If something goes wrong we tell you exactly what and why — no silent failures.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SourcesPickerModal({ sources, initialQuery, onClose, onPick, onConnectNew, fmtAge }: {
  sources: Source[];
  initialQuery: string;
  onClose: () => void;
  onPick: (sourceId: string) => void;
  onConnectNew: () => void;
  fmtAge: (iso: string) => string;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [filter, setFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [sort, setSort] = useState<'recent' | 'name'>('recent');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const activeCount = sources.filter(s => s.status !== 'archived').length;
  const archivedCount = sources.length - activeCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = sources.filter(s => {
      if (filter === 'active' && s.status === 'archived') return false;
      if (filter === 'archived' && s.status !== 'archived') return false;
      if (!q) return true;
      const tagsStr = Object.entries(s.tags ?? {}).map(([k, v]) => `${k}=${v}`).join(' ').toLowerCase();
      return s.name.toLowerCase().includes(q) || s.source_id.toLowerCase().includes(q) || tagsStr.includes(q);
    });
    list = [...list].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      return a.created_at < b.created_at ? 1 : -1; // recent first
    });
    return list;
  }, [sources, query, filter, sort]);

  return (
    <>
      <div className="sp-backdrop" onClick={onClose} />
      <div className="sp-modal" role="dialog" aria-label="All sources" onClick={e => e.stopPropagation()}>
        <div className="sp-head">
          <div>
            <div className="sp-eyebrow"><span className="dot" /> Your sources</div>
            <div className="sp-title">All connected sources</div>
            <div className="sp-sub">
              <strong>{sources.length}</strong> total
              <span className="sp-sep">·</span>
              <span className="sp-ok">● {activeCount} active</span>
              {archivedCount > 0 && (
                <>
                  <span className="sp-sep">·</span>
                  <span className="sp-muted">◌ {archivedCount} archived</span>
                </>
              )}
            </div>
          </div>
          <button className="sp-close" onClick={onClose} aria-label="Close" title="Close (Esc)">✕</button>
        </div>

        <div className="sp-toolbar">
          <div className="sp-search">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <circle cx="5.5" cy="5.5" r="3.5" stroke="currentColor" strokeWidth="1.3" />
              <path d="M8.2 8.2l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, id, or tag…"
            />
            {query && <button className="sp-search-clear" onClick={() => setQuery('')}>clear</button>}
          </div>
          <div className="sp-segmented">
            {(['all', 'active', 'archived'] as const).map(f => (
              <button key={f} data-active={filter === f} onClick={() => setFilter(f)}>
                {f}
                <span className="sp-seg-count">{f === 'all' ? sources.length : f === 'active' ? activeCount : archivedCount}</span>
              </button>
            ))}
          </div>
          <div className="sp-sort">
            <label htmlFor="sp-sort">Sort</label>
            <select id="sp-sort" value={sort} onChange={e => setSort(e.target.value as 'recent' | 'name')}>
              <option value="recent">Recent</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>

        <div className="sp-list-head">
          <span>Source</span>
          <span>Tags</span>
          <span className="sp-col-last">Created</span>
          <span />
        </div>

        <div className="sp-list">
          {filtered.length === 0 ? (
            <div className="sp-empty">
              <div className="sp-empty-title">No sources match "{query}"</div>
              <div className="sp-empty-sub">Try a shorter query or clear filters.</div>
              <button className="sp-empty-clear" onClick={() => { setQuery(''); setFilter('all'); }}>Clear search + filter</button>
            </div>
          ) : (
            filtered.map(s => (
              <button
                key={s.source_id}
                className="sp-row"
                onClick={() => onPick(s.source_id)}
                title={`Open ${s.name}`}
              >
                <span className="sp-col-name">
                  <span className="sp-dot" data-active={s.status !== 'archived'} />
                  <span className="sp-name">{s.name}</span>
                  {s.status === 'archived' && <span className="sp-pill">archived</span>}
                </span>
                <span className="sp-col-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {Object.keys(s.tags ?? {}).length === 0
                    ? <span style={{ color: 'var(--ink-3)' }}>—</span>
                    : Object.entries(s.tags ?? {}).map(([k, v]) => (
                        <TagChip key={k} k={k} v={v} compact />
                      ))}
                </span>
                <span className="sp-col-last">{fmtAge(s.created_at)}</span>
                <span className="sp-arrow">→</span>
              </button>
            ))
          )}
        </div>

        <div className="sp-foot">
          <span className="sp-foot-hint">
            <kbd>Esc</kbd> to close · click a row to open
          </span>
          <button className="sp-cta" onClick={onConnectNew}>
            <span>+</span> Connect another source
          </button>
        </div>
      </div>
    </>
  );
}

// ChannelPicker — five cards on the onboarding screen so the operator
// picks how data is going to come in BEFORE creating the source. The
// backend provisions all five inputs regardless; this is a UX hint
// (?via=<kind> on the redirect) so the source page can auto-open the
// matching config (form schema builder, schedules tab, etc).
function ChannelPicker({ selected, onPick }: { selected: ChannelKind; onPick: (k: ChannelKind) => void }) {
  type Card = { kind: ChannelKind; name: string; blurb: string; icon: React.ReactNode; pill?: 'instant' | 'pull' };
  const cards: Card[] = [
    {
      kind: 'webhook',
      name: 'Inbound webhook',
      blurb: 'Any system POSTs JSON, XML, CSV or form data to a private URL.',
      pill: 'instant',
      icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M12 4l3 3-3 3M15 7H8a4 4 0 0 0-4 4v0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><circle cx="4" cy="14" r="2" stroke="currentColor" strokeWidth="1.4"/></svg>,
    },
    {
      kind: 'rest',
      name: 'REST API pull',
      blurb: 'Fetch a JSON URL on a schedule. Headers + bearer/basic auth supported.',
      pill: 'pull',
      icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3.5" width="14" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M5 8h8M5 11h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
    },
    {
      kind: 'form',
      name: 'Public upload form',
      blurb: 'Hand a partner a URL — they fill the form, submission becomes an event.',
      pill: 'instant',
      icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="2" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M6 6h6M6 9h6M6 12h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
    },
    {
      kind: 'email',
      name: 'Email parse',
      blurb: 'Forward to a private inbox. Body or attachments become events.',
      pill: 'instant',
      icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M2.5 4.5l6.5 5.5 6.5-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      kind: 'upload',
      name: 'File upload',
      blurb: 'POST CSV / JSON / ZIP / xlsx to the per-source upload URL. Rows fan out as events.',
      pill: 'instant',
      icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 3h7l3 3v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M11 3v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M9 13V8M7 10l2-2 2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
  ];
  return (
    <div style={{ maxWidth: 880, margin: '0 auto 24px' }}>
      <div style={{ fontSize: 11.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', textAlign: 'left', marginBottom: 8 }}>
        How is data coming in?
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
        {cards.map(c => {
          const active = selected === c.kind;
          return (
            <button
              key={c.kind}
              type="button"
              onClick={() => onPick(c.kind)}
              aria-pressed={active}
              style={{
                textAlign: 'left',
                padding: 12,
                background: active ? 'var(--accent-soft)' : 'var(--bg-elev)',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--ink)',
                outline: 'none',
                transition: 'border-color 100ms, background 100ms',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ display: 'inline-flex', color: active ? 'var(--accent)' : 'var(--ink-2)' }}>{c.icon}</span>
                <strong style={{ fontSize: 13 }}>{c.name}</strong>
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9.5, padding: '1px 5px', borderRadius: 3, background: 'var(--bg-sunken)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {c.pill}
                </span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.45 }}>{c.blurb}</div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
        All 5 channels are provisioned on every Source — pick the primary one and you'll land directly on its setup.
      </div>
    </div>
  );
}

// ConnectorProfilePicker — six built-in tool profiles + Generic +
// Custom. Drives the discriminator path that auto-connect-bq uses to
// fan out per-event_type BQ views without operator configuration.
//
// BACKLOG: Minimum-action onboarding · Connector profile registry.
function ConnectorProfilePicker({ selected, onPick }: { selected: string; onPick: (id: string) => void }): React.ReactElement {
  const profiles: Array<{ id: string; name: string; blurb: string }> = [
    { id: 'generic_webhook', name: 'Generic webhook', blurb: 'Any JSON POST. We auto-detect the event type from your payload — works out of the box for most tools.' },
    { id: 'pipedrive', name: 'Pipedrive', blurb: 'CRM webhooks. Discriminator: payload.event (deal.added, person.updated, …).' },
    { id: 'stripe', name: 'Stripe', blurb: 'Payments. Discriminator: payload.type (charge.succeeded, invoice.paid, …).' },
    { id: 'hubspot', name: 'HubSpot', blurb: 'CRM property-change subscriptions. Discriminator: subscriptionType.' },
    { id: 'treblle', name: 'Treblle (API observability)', blurb: 'API request/response captures from the Treblle SDK. Highest-volume input — consider sampling.' },
    { id: 'custom', name: 'Custom / other', blurb: 'I\'ll wire the discriminator manually after the source is created.' },
  ];
  return (
    <div style={{ marginTop: 24, marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--ink-3)', marginBottom: 12, textAlign: 'center' }}>
        WHICH TOOL IS SENDING THE DATA?
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        {profiles.map(p => {
          const active = p.id === selected;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p.id)}
              style={{
                textAlign: 'left',
                padding: '12px 14px',
                background: active ? 'var(--accent-soft)' : 'var(--bg-elev)',
                border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--ink)',
                display: 'flex', flexDirection: 'column', gap: 4,
                transition: 'background 100ms, border 100ms',
              }}
            >
              <strong style={{ fontSize: 13 }}>{p.name}</strong>
              <span style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.4 }}>{p.blurb}</span>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
        We pre-fill the discriminator + suggested merge keys for the picked tool. You can change this later or pick &quot;Custom&quot; to bypass.
      </div>
    </div>
  );
}

